import { ActionTree } from 'vuex';
import { AuthState } from './types';
import { RootState } from '../../types';
import { defaultBackendAccount } from '../../../backend/account'; //TODO @/shared/backend/account
import { i18nGlobal } from '../../../../i18n';
import { Credentials } from '../../../model/credentials';
import store from '../..';
import { Logger } from 'fsts';
import moduleUtils from '../moduleUtils';
import GeneralUtils from '@/shared/utils/generalUtils';
import { defaultBackendReferenceParent } from '@/shared/backend/referenceParent';
import { ReferenceParentTable } from '@/shared/model/referenceParent';
import { AxiosResponse } from 'axios';
import { Value } from '@/shared/model/referenceParent';
import applicationUser, {
  ApplicationUser,
} from '@/shared/model/applicationUser';

const logger = new Logger('actions');
export const actions: ActionTree<AuthState, RootState> = {
  /**
   * Login to the backend.
   */
  async login({ commit, dispatch }, payload: Credentials) {
    // authenticate to get the access token
    return defaultBackendAccount
      .authenticate(payload.username!, payload.password!, payload.rememberMe)
      .then((response) => {
        commit('setToken', response.data);
        commit('setAccountId', response.data.userId);
        return response;
      })
      .catch((e) => {
        logger.error(e);
        throw e;
      });
  },

  async googleLogin({ commit, dispatch }, payload: string) {
    // authenticate to get the access token
    logger.log('googleLogin');
    return defaultBackendAccount
      .authenticateGoogle(payload)
      .then((response) => {
        commit('setToken', response.data);
        commit('setIsGoogleLogin', true);
        commit('setAccountId', response.data.userId);
        return response;
      })
      .catch((e) => {
        logger.error(e);
        throw e;
      });
  },

  async facebookLogin({ commit, dispatch }, payload: string) {
    // authenticate to get the access token
    logger.log('facebookLogin');
    return defaultBackendAccount
      .authenticateFacebook(payload)
      .then((response) => {
        commit('setToken', response.data);
        commit('setIsFacebookLogin', true);
        commit('setAccountId', response.data.userId);
        return response;
      })
      .catch((e) => {
        logger.error(e);
        throw e;
      });
  },

  async passwordForgotten({ commit, dispatch }, email: string) {
    try {
      const status = (await defaultBackendAccount.initResetPassword(email))
        .status;
    } catch (e) {
      commit('showError', i18nGlobal.t(`pwd_forgotten.pwd_forgotten_failed`), {
        root: true,
      });
    }
  },
  async loadAccountDetails({ commit, dispatch }, id?: number) {
    return defaultBackendAccount
      .getMyAccount()
      .then((result) => {
        commit('setAccount', result.data.account);
        commit('setAccountId', result.data.account.id);
        commit('setCompanyId', result.data.company.id);
        commit('setSubscriptionInfo', result.data.company);
        commit('setPermissions', result.data.permissions);
        //success('auth.acc_load.success', commit)
      })
      .catch((e) => {
        commit('setAccount', undefined);
      });
  },
  /**
   * Clears the access token, effectively logging out the user.
   */
  logout({ commit }) {
    return defaultBackendAccount.logout().then((result) => {
      commit('setAccount', undefined);
      commit('setAccountId', null);
      commit('clearTokens');
      return result;
    });
  },
  manualPasswordReset({ commit }, applicationUserId: string) {
    return defaultBackendAccount
      .manualPasswordReset(applicationUserId)
      .then(() => {
        moduleUtils.ok(i18nGlobal.t(`success.manual_password_reset`), commit);
      })
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  createAdmin({ commit, dispatch }, payload: any) {
    return defaultBackendAccount
      .createAdmin(payload)
      .then((response) => {
        commit(
          'showSuccess',
          i18nGlobal.t(`createAdmin_management.success.company_created`),
          { root: true }
        );
      })
      .catch((error) => {
        console.log(error);
        commit(
          'showError',
          i18nGlobal.t(`createAdmin_management.error.company_created`),
          { root: true }
        );
      });
  },
  createContact({ commit, dispatch }, payload: any) {
    return defaultBackendAccount
      .createContact(payload)
      .then((response) => {
        commit(
          'showSuccess',
          i18nGlobal.t(`employee_management.success.create_employee`),
          { root: true }
        );
      })
      .catch((error) => {
        console.log(error);
        commit(
          'showError',
          i18nGlobal.t(`employee_management.error.create_employee`),
          { root: true }
        );
      });
  },
  resendConfirmationEmail({ commit, dispatch }, id: number) {
    return defaultBackendAccount
      .resendConfirmationEmail(id)
      .then((response) => {
        commit(
          'showSuccess',
          i18nGlobal.t(`success.resend_confirmation_email`),
          { root: true }
        );
      })
      .catch((error) => {
        console.log(error);
        commit('showError', i18nGlobal.t(`error.resend_confirmation_email`), {
          root: true,
        });
      });
  },

  async impersonateUser({ commit }, userId: string) {
    return defaultBackendAccount
      .impersonateUser(userId)
      .then((response) => {
        commit('setToken', response.data);
        commit('setAccountId', response.data.userId);
        return response;
      })
      .catch((e) => {
        logger.error(e);
      });
  },

  async isResponsibleOrDeputy({ commit, dispatch }) {
    try {
      const response = await defaultBackendAccount.isResponsibleOrDeputy();
      commit('setIsResponsibleOrDeputy', response.data);
    } catch (e) {
      console.log(e);
      commit('setIsResponsibleOrDeputy', {});
      commit('showError', i18nGlobal.t(`error.load_data_failed`), {
        root: true,
      });
    }
  },
  isUniqueEmail({ commit, dispatch }, payload: { email: string }) {
    return defaultBackendAccount
      .isUniqueEmail(payload.email)
      .then((response) => {
        return response.data;
      })
      .catch((e) => {
        moduleUtils.error(
          i18nGlobal.t(`error.check_is_unique_email`),
          commit,
          e,
          logger
        );
        throw e;
      });
  },
  resetPasswordByEmail(
    { commit, dispatch },
    payload: {
      Email: string;
      UserName: string;
    }
  ) {
    return defaultBackendAccount
      .resetPasswordByEmail(payload)
      .then(() => {})
      .catch((e) => {
        // for security reasons (leak user emails) don't show any info except errors for developer
        if (GeneralUtils.isDevEnvironment()) {
          logger.debug('resetPasswordByEmail :>> ');
          logger.error(e);
        }
      });
  },
  setLanguage({ commit }, lang: string) {
    return defaultBackendAccount.setLanguage(lang).catch((e) => {
      moduleUtils.error('error', commit, e, logger);
      throw e;
    });
  },
  getProfileInfo({ commit }, attendeeId?: string) {
    return defaultBackendAccount
      .getProfileInfo(attendeeId)
      .then((x) => x.data)
      .catch((e) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  updateProfile({ commit }, profileData: ApplicationUser) {
    return defaultBackendAccount
      .updateProfile(profileData)
      .then(() => {
        moduleUtils.ok(i18nGlobal.t(`success.profile_update`), commit);
      })
      .catch((e) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  updatePassword({ commit }, newPassword: string) {
    return defaultBackendAccount
      .updatePassword(newPassword)
      .then(() => {
        moduleUtils.ok(i18nGlobal.t(`success.password_changed`), commit);
      })
      .catch((e) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
  getRandomPassword({ commit }) {
    return defaultBackendAccount
      .getRandomPassword()
      .then((response: AxiosResponse<string>) => {
        return response.data;
      })
      .catch((e: any) => {
        moduleUtils.error('error', commit, e, logger);
        throw e;
      });
  },
};
